import { useContext } from "react";
import { UseQueryResult, useQuery } from "@tanstack/react-query";

import { AxiosContext } from "contexts/AxiosContext";
import { ICasesResponse } from "interfaces/case";
import { makePostBody } from "core/makeCaseTablePayload";
import {
  getCaseTableMappedFilterArgs,
  getCaseTableMappedSortArgs
} from "hooks/case-management/useGetCaseTableData";

interface IPostTableQuery {
  pageIndex: number;
  rowsPerPage: number;
  sortBy: any;
  filters: any;
  initialFetch: boolean;
}

/**
 * The usePostTableQuery function is designed to post query parameters to the API instead of using a GET request.
 * This is was done to overcome the 128 character URL limit set by the API gateway which is no longer used.
 * Eventually, the API could be reverted to support larger URLs and this function could be replaced in favor of a GET request.
 */
export const usePostTableQuery = ({
  pageIndex,
  rowsPerPage,
  sortBy,
  filters,
  initialFetch
}: IPostTableQuery): UseQueryResult<ICasesResponse, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  if (initialFetch) pageIndex = 1;
  else pageIndex += 1;

  const payload = makePostBody({
    page: pageIndex,
    size: rowsPerPage,
    ...getCaseTableMappedFilterArgs(filters),
    ...getCaseTableMappedSortArgs(sortBy)
  });

  return useQuery({
    queryKey: ["cases", payload],
    queryFn: async () => {
      const { data } = await axios.post<ICasesResponse>(
        `cases/filter_cases`,
        payload
      );

      return data;
    }
  });
};
